import { HumanisedDate } from 'module/ui/components/atoms/humanised-date';
import { ColumnType } from 'module/ui/components/molecules/draggable-table/types';
import { SimpleTable } from 'module/ui/components/molecules/simple-table';
import { SignalOccurenceLight } from 'api/all-signals/types';
import { ReportedBy } from 'api/timeline/types';
import moment from 'moment';
import { FormattedMessage } from 'react-intl';
import {
  StyledDraggableTable,
  SignalName,
  CompanyName,
  Reason,
  SaidBy,
  Role,
  CreatedOn,
} from './all-signals-table.styles';
import { formatUserDisplayName } from '../signal-output/utils';

export const AllSignalsTable: React.FC<{
  signals: SignalOccurenceLight[];
  loading?: boolean;
}> = ({ signals, loading }) => {
  const columns: ColumnType<SignalOccurenceLight>[] = [
    {
      key: 'signalName',
      title: <FormattedMessage id="all_signals.table.signal_name" />,
      width: '200px',
      render: (value) => <SignalName>{value}</SignalName>,
    },
    {
      key: 'company',
      title: <FormattedMessage id="all_signals.table.company" />,
      width: '200px',
      render: (_, record) => {
        const reportedBy =
          Array.isArray(record?.reportedBy) && record?.reportedBy?.length > 1
            ? record?.reportedBy?.[0]
            : record?.reportedBy;

        return (
          <CompanyName>
            {(reportedBy as unknown as ReportedBy)?.company?.companyName || '-'}
          </CompanyName>
        );
      },
    },
    {
      key: 'reason',
      title: <FormattedMessage id="reason" />,
      width: '200px',
      render: (_, record) => {
        const reason = record?.fields?.find(
          (field) =>
            Object.keys(record?.metaData)[0] === String(field?.fieldId),
        );

        if (reason) {
          return reason?.metaData?.options?.find(
            (option) => option.value === reason?.fieldValue,
          )?.label ? (
            <Reason>
              {
                reason?.metaData?.options?.find(
                  (option) => option.value === reason?.fieldValue,
                )?.label
              }
            </Reason>
          ) : (
            <Reason>-</Reason>
          );
        }

        return <Reason>-</Reason>;
      },
    },
    {
      key: 'saidBy',
      title: <FormattedMessage id="all_signals.table.said_by" />,
      width: '200px',
      render: (_, record) => {
        const reportedBy =
          Array.isArray(record?.reportedBy) && record?.reportedBy?.length > 1
            ? record?.reportedBy?.[0]
            : record?.reportedBy;

        return (
          <SaidBy>{formatUserDisplayName(reportedBy as ReportedBy)}</SaidBy>
        );
      },
    },
    {
      key: 'role',
      title: <FormattedMessage id="all_signals.table.role" />,
      width: '75px',
      render: (_, record) => {
        const reportedBy =
          Array.isArray(record?.reportedBy) && record?.reportedBy?.length > 1
            ? record?.reportedBy?.[0]
            : (record?.reportedBy as unknown as ReportedBy);

        return <Role>{reportedBy?.role?.roleName || '-'}</Role>;
      },
    },
    {
      key: 'createdOn',
      title: <FormattedMessage id="all_signals.table.created_on" />,
      width: '75px',
      render: (_, record) => (
        <CreatedOn>
          <HumanisedDate timestamp={moment(record?.createdAt)} />
        </CreatedOn>
      ),
    },
  ];

  return (
    <StyledDraggableTable>
      <SimpleTable<SignalOccurenceLight & { id: string }>
        columns={columns}
        data={signals.map((signal) => ({
          ...signal,
          id: String(signal.signalOccurrenceId),
        }))}
        loading={loading}
      />
    </StyledDraggableTable>
  );
};
